import AbandonedCall from 'models/abandoned_call';
import AIActivityItem from 'models/ai_activity/ai_activity_item';
import AIAssistantMessage from 'models/ai_assistant_message';
import AICyberThreadsGuardrailResult from 'models/ai_cyber_threads_guardrail_result';
import AIFunctionCallResults from 'models/ai_function_call_results';
import AIQualityCheck from 'models/ai_quality_check';
import AITransfer from 'models/ai_transfer';
import ChatCustomerUrl from 'models/chat_customer_url';
import ChatMessage from 'models/chat_message';
import Communicator from 'models/communicator';
import ContentPlaceholder from 'models/content_placeholder';
import ConversationMessage from 'models/conversation_message';
import ConversationNote from 'models/conversation_note';
import CustomChannelMessage from 'models/custom_channel_message';
import CustomerActivity from 'models/customer_activity';
import EmailCampaignOutreach from 'models/email_campaign_outreach';
import EmailMessage from 'models/email_message';
import EmailPreview from 'models/content_preview/email_preview';
import EmailSendFailure from 'models/email_send_failure';
import FbMessageIncoming from 'models/fb_message_incoming';
import FbMessageOutgoing from 'models/fb_message_outgoing';
import getClassName from 'scripts/lib/class_name';
import { isDevelopment } from 'scripts/application/lib/environment';
import MessageAutomationSummaryContent, {
  MessageAutomationContent,
  MessageAutomationItemContent,
} from 'models/message_automation_content';
import PaymentStatusEventContent from 'models/payment_status_event_content';
import PhoneCall from 'models/phone_call';
import PhoneCallback from 'models/phone_callback';
import SmsCampaignOutreach from 'models/sms_campaign_outreach';
import SmsMessage from 'models/sms_message';
import Task from 'models/task';
import TaskCompletionContent from 'models/task_completion_content';
import TaskReopenedContent from 'models/task_reopened_content';
import Tweet from 'models/tweet';
import SecureDataRequest from 'models/secure_data_request';
import Voicemail from 'models/voicemail';
import WebForm from 'models/web_form';
import WidgetActivity from 'models/widget_activity';
import VoiceAIMessage from 'models/voiceai_message';

export function getDefaultTopicName(firstItem, lastManualItem) {
  // LOADING STATE
  if (firstItem && firstItem.content instanceof ContentPlaceholder) {
    return 'Loading conversation';
  }

  // NO MANUAL CONVERSATION ITEMS
  if (!lastManualItem) {
    return 'New conversation';
  }

  return getTopicNameFromItem(lastManualItem);
}

// Fallback used when no i18n `t` is provided (fake backend, tests). Mirrors i18next's
// `{{var}}` interpolation so callers without translation still get the English string.
function defaultTranslate(key, options) {
  if (!options) {
    return key;
  }
  return key.replace(/{{(\w+)}}/g, (match, name) => (name in options ? options[name] : match));
}

export function getTopicNameFromItem(conversationItem, customChannelNames, t = defaultTranslate) {
  if (!conversationItem) {
    return t('New Conversation');
  }
  let content = conversationItem.content;

  let isIncoming = conversationItem.initiator.type === Communicator.CUSTOMER;

  if (content instanceof AbandonedCall) {
    return t('Abandoned Call');
  } else if (content instanceof AIActivityItem) {
    return t('AI Activity');
  } else if (content instanceof AIAssistantMessage) {
    return t('AI Assistant Message');
  } else if (content instanceof AICyberThreadsGuardrailResult) {
    return t('AI Cyber Threads Guardrail Result');
  } else if (content instanceof AIFunctionCallResults) {
    return t('AI Function Call Results');
  } else if (content instanceof AIQualityCheck) {
    return t('AI Quality Check');
  } else if (content instanceof AITransfer) {
    return t('AI Transfer');
  } else if (content instanceof MessageAutomationSummaryContent) {
    return t('Message Automation Summary');
  } else if (content instanceof MessageAutomationItemContent) {
    return t('Message Automation Item');
  } else if (content instanceof MessageAutomationContent) {
    return t('Message Automation Content');
  } else if (content instanceof ChatMessage) {
    return isIncoming ? t('Incoming Chat') : t('Outgoing Chat');
  } else if (content instanceof ChatCustomerUrl) {
    return t('Chat Customer URL');
  } else if (content instanceof ConversationMessage) {
    return isIncoming
      ? t('Incoming {{channel}}', { channel: content.getChannelName() })
      : t('Outgoing {{channel}}', { channel: content.getChannelName() });
  } else if (content instanceof ConversationNote) {
    return t('Note');
  } else if (content instanceof CustomChannelMessage) {
    const customChannelName = customChannelNames && customChannelNames[content.customChannelId];
    if (!customChannelName) {
      return isIncoming ? t('Incoming Custom Channel Message') : t('Outgoing Custom Channel Message');
    }
    return isIncoming
      ? t('Incoming {{channel}} Message', { channel: customChannelName })
      : t('Outgoing {{channel}} Message', { channel: customChannelName });
  } else if (content instanceof CustomerActivity) {
    let sourceName = content.sourceName || t('Customer');
    return t('{{sourceName}} Activity', { sourceName });
  } else if (content instanceof EmailCampaignOutreach) {
    return isIncoming ? t('Incoming Email') : t('Outgoing Email');
  } else if (content instanceof EmailMessage) {
    return isIncoming ? t('Incoming Email') : t('Outgoing Email');
  } else if (content instanceof EmailSendFailure) {
    return t('Email Send Failure');
  } else if (content instanceof EmailPreview) {
    return isIncoming ? t('Incoming Email') : t('Outgoing Email');
  } else if (content instanceof FbMessageIncoming) {
    return t('Incoming Facebook Message');
  } else if (content instanceof FbMessageOutgoing) {
    return t('Outgoing Facebook Message');
  } else if (content instanceof PhoneCall) {
    return isIncoming ? t('Incoming Call') : t('Outgoing Call');
  } else if (content instanceof PhoneCallback) {
    return t('Call Customer Back');
  } else if (content instanceof SmsCampaignOutreach) {
    return isIncoming ? t('Incoming SMS') : t('Outgoing SMS');
  } else if (content instanceof SmsMessage) {
    return isIncoming ? t('Incoming SMS') : t('Outgoing SMS');
  } else if (content instanceof Task) {
    return t('Task Created');
  } else if (content instanceof TaskCompletionContent) {
    return t('Task Closed');
  } else if (content instanceof TaskReopenedContent) {
    return t('Task Reopened');
  } else if (content instanceof Tweet) {
    if (isIncoming) {
      return `@${content.user.screenName}`;
    }
    return t('Outgoing Tweet');
  } else if (content instanceof SecureDataRequest) {
    return t('Credit Card Request');
  } else if (content instanceof PaymentStatusEventContent) {
    return t('Credit Card Request');
  } else if (content instanceof Voicemail) {
    return t('Voicemail');
  } else if (content instanceof WebForm) {
    return t('Incoming Web Form');
  } else if (content instanceof WidgetActivity) {
    return t('Widget Activity');
  } else if (content instanceof VoiceAIMessage) {
    // we display the most recent transcript message for ongoing automated calls
    // and for automated calls that did not result in handoff
    return t('Automated Call');
  }

  const devMode = isDevelopment();
  if (!devMode) return null; // Do not break UI in prod over some unknown conversation item. Doing it in dev is OK

  throw new Error(`invalid conversation content [${getClassName(content)}]`);
}
